import { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';

const PADDLE_CLIENT_TOKEN = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    color: 'var(--text-3)',
    features: [
      '3 meetings per month',
      'AI summary & decisions',
      'Action items with owners',
      'Urgency tags (🔴🟡🟢)',
      'Blockers & open questions',
      'Paste transcript',
      'PDF export',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 19,
    color: 'var(--accent)',
    popular: true,
    features: [
      'Unlimited meetings',
      'AI summary & decisions',
      'Action items with owners',
      'Urgency tags (🔴🟡🟢)',
      'Blockers & open questions',
      'Paste transcript',
      'PDF export',
      'Email team export (coming soon)',
      'Zoom import (coming soon)',
    ],
    cta: 'Upgrade to Pro',
  },
];

function usePaddle(onSuccess, onClose) {
  const paddleRef = useRef(null);
  const [ready, setReady] = useState(false);
  const onSuccessRef = useRef(onSuccess);
  const onCloseRef = useRef(onClose);
  onSuccessRef.current = onSuccess;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (paddleRef.current) { setReady(true); return; }

    function initPaddle() {
      if (!window.Paddle) return;
      window.Paddle.Setup({
        token: PADDLE_CLIENT_TOKEN,
        eventCallback: (event) => {
          if (event.name === 'checkout.completed') {
            onSuccessRef.current(event.data?.transaction_id);
          }
          if (event.name === 'checkout.closed') {
            onCloseRef.current();
          }
        },
      });
      paddleRef.current = window.Paddle;
      setReady(true);
    }

    if (window.Paddle) { initPaddle(); return; }

    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.onload = initPaddle;
    document.head.appendChild(script);
  }, []); // eslint-disable-line

  return { ready };
}

function SuccessBanner({ onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 6000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div style={{
      background: 'rgba(0,255,136,0.08)',
      border: '1px solid rgba(0,255,136,0.3)',
      borderRadius: 12, padding: '1.25rem 1.5rem',
      marginBottom: '1.5rem', display: 'flex',
      alignItems: 'center', gap: '1rem', animation: 'fadeIn 0.4s ease',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%',
        background: 'rgba(0,255,136,0.15)', border: '1px solid rgba(0,255,136,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 18, flexShrink: 0,
      }}>✓</div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)', marginBottom: 2 }}>
          You're now on Pro 🎉
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5 }}>
          Unlimited meetings, PDF export, Zoom import and email summaries are all unlocked.
        </div>
      </div>
      <button onClick={onDismiss} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 18, flexShrink: 0 }}>×</button>
    </div>
  );
}

export default function PricingPage({ onNavigate }) {
  const { user } = useUser();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSuccess = (txnId) => {
    console.log('[Paddle] checkout.completed txn:', txnId);
    setSuccess(true);
    setLoading(null);
    setTimeout(fetchStatus, 3000);
  };

  const handleClose = () => {
    setLoading(null);
  };

  const { ready: paddleReady } = usePaddle(handleSuccess, handleClose);

  useEffect(() => { fetchStatus(); }, [user?.id]); // eslint-disable-line

  const fetchStatus = () => {
    if (!user?.id) return;
    fetch('/api/payments/status', { headers: { 'x-user-id': user.id } })
      .then(r => r.json()).then(setSubscription).catch(console.error);
  };

  const handleUpgrade = async () => {
    if (!paddleReady) { setError('Payment system still loading, please try again.'); return; }
    setLoading('pro');
    setError('');
    try {
      const res = await fetch('/api/payments/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      const txnId = data.transactionId ||
        new URL(data.checkoutUrl).searchParams.get('_ptxn') ||
        data.checkoutUrl.match(/txn_[a-z0-9]+/i)?.[0];

      if (!txnId) throw new Error('Could not get transaction ID from Paddle');

      window.Paddle.Checkout.open({
        transactionId: txnId,
        settings: { displayMode: 'overlay', theme: 'dark', locale: 'en' },
      });
    } catch (err) {
      setError(err.message);
      setLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your Pro subscription?')) return;
    setLoading('cancel');
    try {
      await fetch('/api/payments/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
      });
      fetchStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(null);
    }
  };

  const handlePortal = async () => {
    setLoading('portal');
    try {
      const res = await fetch('/api/payments/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      window.open(data.portalUrl, '_blank', 'noopener');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(null);
    }
  };

  const activePlan = subscription?.plan || 'free';

  return (
    <div className="page fade-in">
      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }`}</style>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 6 }}>Pricing</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Simple pricing. No hidden fees. Taxes handled automatically.</div>
      </div>

      {success && <SuccessBanner onDismiss={() => { setSuccess(false); fetchStatus(); }} />}
      {error && (
        <div style={{ background: 'var(--red-dim)', border: '1px solid rgba(255,77,106,0.25)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.25rem', color: 'var(--red)', fontSize: 13 }}>
          ⚠ {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', maxWidth: 700 }} className="grid-pricing">
        {PLANS.map(plan => {
          const isActive = activePlan === plan.id;
          const isLoadingThis = loading === plan.id;
          return (
            <div key={plan.id} style={{
              background: plan.popular ? 'var(--bg-3)' : 'var(--bg-2)',
              border: `1px solid ${isActive ? plan.color : plan.popular ? 'rgba(0,255,136,0.3)' : 'var(--border)'}`,
              borderRadius: 16, padding: '1.75rem', position: 'relative',
              display: 'flex', flexDirection: 'column', gap: '1.25rem', transition: 'all 0.2s',
            }}>
              {plan.popular && !isActive && (
                <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'var(--accent)', color: '#000', fontSize: 10, fontWeight: 700, fontFamily: 'var(--mono)', padding: '3px 14px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>Most Popular</div>
              )}
              {isActive && (
                <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: plan.color, color: plan.id === 'free' ? 'var(--bg)' : '#000', fontSize: 10, fontWeight: 700, fontFamily: 'var(--mono)', padding: '3px 14px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>Current Plan</div>
              )}
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: plan.color, marginBottom: 10 }}>{plan.name}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: 36, fontWeight: 700, fontFamily: 'var(--mono)' }}>${plan.price}</span>
                  {plan.price > 0 && <span style={{ fontSize: 13, color: 'var(--text-3)' }}>/month</span>}
                </div>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {plan.features.map(f => (
                  <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: 13, color: 'var(--text-2)' }}>
                    <span style={{ color: plan.color, flexShrink: 0 }}>✓</span>{f}
                  </div>
                ))}
              </div>
              {plan.id === 'free' ? (
                <button disabled style={{ width: '100%', padding: '0.75rem', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-3)', fontSize: 13, fontWeight: 600, cursor: 'default' }}>
                  {isActive ? '✓ Current Plan' : 'Free Forever'}
                </button>
              ) : isActive ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <button disabled style={{ width: '100%', padding: '0.75rem', borderRadius: 10, border: `1px solid ${plan.color}`, background: 'transparent', color: plan.color, fontSize: 13, fontWeight: 600, cursor: 'default' }}>✓ Active</button>
                  <button onClick={handlePortal} disabled={loading === 'portal'} style={{ width: '100%', padding: '0.5rem', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', fontSize: 12, cursor: 'pointer' }}>
                    {loading === 'portal' ? 'Opening...' : 'Manage billing ↗'}
                  </button>
                  <button onClick={handleCancel} disabled={loading === 'cancel'} style={{ width: '100%', padding: '0.5rem', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--red)', fontSize: 12, cursor: 'pointer' }}>
                    {loading === 'cancel' ? 'Cancelling...' : 'Cancel subscription'}
                  </button>
                </div>
              ) : (
                <button onClick={handleUpgrade} disabled={isLoadingThis || !paddleReady} style={{ width: '100%', padding: '0.75rem', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#000', fontSize: 13, fontWeight: 700, cursor: isLoadingThis || !paddleReady ? 'not-allowed' : 'pointer', opacity: isLoadingThis || !paddleReady ? 0.7 : 1, boxShadow: '0 0 20px rgba(0,255,136,0.2)', transition: 'all 0.2s' }}>
                  {isLoadingThis ? '⟳ Opening checkout...' : !paddleReady ? 'Loading...' : plan.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: '2rem', fontSize: 12, color: 'var(--text-3)' }}>
        🔒 Payments securely processed by Paddle · Taxes handled automatically · Cancel anytime
      </div>
    </div>
  );
}
